import { useMutation, useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client } from "../client";

const sso = client.admin.workspace[":id"].sso;
const key = (workspaceId: string) => ["admin", "workspace", workspaceId, "sso"];
const refresh = (workspaceId: string) => queryClient.invalidateQueries({ queryKey: key(workspaceId) });

export type SsoSettings = Parameters<typeof sso.$put>[0]["json"];
export type SsoSetupIntent = Parameters<typeof sso.invite.$post>[0]["json"]["intents"][number];

export const useWorkspaceSso = (workspaceId: string) =>
  useQuery({
    queryKey: key(workspaceId),
    queryFn: async () => {
      const res = await sso.$get({ param: { id: workspaceId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });

export const useSaveSsoSettings = (workspaceId: string) =>
  useMutation({
    mutationFn: async (json: SsoSettings) => {
      const res = await sso.$put({ param: { id: workspaceId }, json });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => refresh(workspaceId),
  });

export const useAddSsoDomain = (workspaceId: string) =>
  useMutation({
    mutationFn: async (domain: string) => {
      const res = await sso.domains.$post({ param: { id: workspaceId }, json: { domain } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => refresh(workspaceId),
  });

export const useRemoveSsoDomain = (workspaceId: string) =>
  useMutation({
    mutationFn: async (domainId: string) => {
      const res = await sso.domains[":domainId"].$delete({ param: { id: workspaceId, domainId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => refresh(workspaceId),
  });

export const useSyncSsoDomains = (workspaceId: string) =>
  useMutation({
    mutationFn: async () => {
      const res = await sso.domains.sync.$post({ param: { id: workspaceId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => refresh(workspaceId),
  });

export const useInviteItContact = (workspaceId: string) =>
  useMutation({
    mutationFn: async (json: { email: string; intents: SsoSetupIntent[] }) => {
      const res = await sso.invite.$post({ param: { id: workspaceId }, json });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
